import React from "react"
import { useTranslation } from "react-i18next"
import { withFormik, FormikProps, Form, Field, FieldProps } from "formik"
import { FormLabel, Box, Switch, Typography, styled } from "@mui/material"

import Button from "components/ui/Submit"
import { Description } from "components/Layout/CookieConsent/elements"
import { CookieConsentValues } from "types"
import { colors } from "theme"

interface FormProps {
  initialValues: CookieConsentValues
  onSubmit: (values: CookieConsentValues) => void
}

type Props = FormikProps<CookieConsentValues> & FormProps

const CookieConsentForm = ({ isSubmitting, onSubmit }: Props) => {
  const { t } = useTranslation()

  const handleClick = () => {
    const values = { required: true, analytics: true, personal: true }
    onSubmit(values)
  }

  return (
    <Form>
      <Content>
        <Description>{t("features.cookie.form.description.text")}</Description>
      </Content>
      <AcceptAll disabled={isSubmitting} onClick={handleClick}>
        {t("features.cookie.form.accept")}
      </AcceptAll>
      <FormGroup>
        <Content>
          <Label>{t("features.cookie.form.required.title")}</Label>
          <Text>{t("features.cookie.form.required.text")}</Text>
        </Content>
        <Field name="required" type="boolean">
          {({ field }: FieldProps) => (
            <Switch {...field} checked={field.value} disabled />
          )}
        </Field>
      </FormGroup>
      <FormGroup>
        <Content>
          <Label>{t("features.cookie.form.analytics.title")}</Label>
          <Text>{t("features.cookie.form.analytics.text")}</Text>
        </Content>
        <Field name="analytics" type="boolean">
          {({ field }: FieldProps) => (
            <Switch {...field} checked={field.value} />
          )}
        </Field>
      </FormGroup>
      <FormGroup>
        <Content>
          <Label>{t("features.cookie.form.personal.title")}</Label>
          <Text>{t("features.cookie.form.personal.text")}</Text>
        </Content>
        <Field name="personal" type="boolean">
          {({ field }: FieldProps) => (
            <Switch {...field} checked={field.value} />
          )}
        </Field>
      </FormGroup>
      <Save type="submit" disabled={isSubmitting}>
        {t("features.cookie.form.save")}
      </Save>
    </Form>
  )
}

const CookieConsentFormWithFormik = withFormik<FormProps, CookieConsentValues>({
  enableReinitialize: true,
  mapPropsToValues: ({ initialValues }) => initialValues,
  handleSubmit: (values, { props: { onSubmit } }) => onSubmit(values),
})(CookieConsentForm)

export default CookieConsentFormWithFormik

const FormGroup = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  justifyContent: "space-between",
  margin: "7px 0",
  [theme.breakpoints.up("md")]: {},
}))

const Content = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up("md")]: {},
}))

const Label = styled(FormLabel)(() => ({
  color: colors.indigo,
  fontSize: "0.875rem",
  fontWeight: "600",
  lineHeight: "17px",
}))

const Text = styled(Typography)(() => ({
  color: colors.black,
  fontSize: "0.6875rem",
  fontWeight: 400,
  lineHeight: "1.66",
}))

const Btn = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  borderRadius: "21px",
  display: "block",
  fontWeight: "bold",
  margin: "16px 0",
  textAlign: "center",
  width: "236px",
}))

const AcceptAll = styled(Btn)(({ theme }) => ({
  background: colors.lightPersianGreen,
  color: theme.palette.primary.main,
}))

const Save = styled(Btn)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  margin: "16px 0 0 0",
}))
